import { View, Text } from '@react-pdf/renderer';
import { styles } from './PDFStyles';
import { getPlanColor } from '@/utils/colors';
import { formatCurrency, type CurrencyCode } from '@/utils/currencyFormatter';
import { type Channel } from '@/utils/previewFormatters';

interface PDFCategoryTableProps {
  channels: Channel[];
  types: Array<{ id: number; name: string }>;
  hasSequenceNumbers: boolean;
  categoryName?: string; // Optional category name for header
  providerId: number; // Add providerId prop
  providerCurrency: CurrencyCode; // Update type to match CurrencyCode
  filterOptions: {
    showDescription: boolean;
    showPrice: boolean;
    showPlans: boolean;
    showInAllCategories: boolean;
    hideSerialNumbers: boolean;
    sortOrder?: 'asc' | 'desc';
  };
}

export function PDFCategoryTable({ 
  channels, 
  types,
  hasSequenceNumbers,
  providerCurrency, // Get currency from props
  filterOptions
}: PDFCategoryTableProps) {
  // Ensure channels is an array and filter out any invalid entries
  const validChannels = Array.isArray(channels) ? channels.filter(channel => channel && channel.id) : [];

  const sortedChannels = hasSequenceNumbers
    ? [...validChannels].sort((a, b) => {
        // For sequential providers, types[0] is the sequence type (ordered by display_order in DB)
        const firstTypeId = types[0]?.id;
        const aSequenceNumber = a.numbers?.find(n => n.type?.id === firstTypeId);
        const bSequenceNumber = b.numbers?.find(n => n.type?.id === firstTypeId);
        const aNum = aSequenceNumber?.number || '0';
        const bNum = bSequenceNumber?.number || '0';
        const comparison = parseInt(aNum, 10) - parseInt(bNum, 10);
        // Apply sort order (asc or desc)
        return filterOptions.sortOrder === 'desc' ? -comparison : comparison;
      })
    : validChannels;

  // Filter out serial number type if hideSerialNumbers is enabled for sequential providers
  const displayTypes = (hasSequenceNumbers && filterOptions.hideSerialNumbers) 
    ? types.slice(1) // Skip the first type (serial number) for sequential providers
    : types;

  // Calculate flex values based on what's displayed
  let nameColumnFlex = 2;
  const numberColumnsFlex = 1;
  const descriptionColumnFlex = filterOptions.showDescription ? 2 : 0;
  const priceColumnFlex = filterOptions.showPrice ? 1 : 0;
  const plansColumnFlex = filterOptions.showPlans ? 1 : 0;
  
  // If we're not showing many columns, make the name column larger
  if (!filterOptions.showDescription && !filterOptions.showPrice && !filterOptions.showPlans) {
    nameColumnFlex = 3;
  }

  // Render a table row for a single channel
  const ChannelRow = ({ channel }: { channel: Channel }) => (
    <View style={styles.tableRow} wrap={false}>
      <View style={[styles.tableCell, { flex: nameColumnFlex }]}>
        <Text>{channel.name || 'Unnamed Channel'}</Text>
      </View>

      {/* Show channel numbers after channel name, filtered based on hideSerialNumbers setting */}
      {displayTypes.map(type => {
        const numberForType = channel.numbers?.find(
          n => n.type?.id === type.id
        );
        const numberValue = numberForType?.number || '-';
        // Ensure we never render an empty string - always use a fallback
        const displayValue = (numberValue && numberValue.toString().trim()) || '-';
        return (
          <View key={type.id} style={[styles.tableCell, { flex: numberColumnsFlex }]}>
            <Text>{displayValue}</Text>
          </View>
        );
      })}

      {filterOptions.showPrice && (
        <View style={[styles.tableCell, { flex: priceColumnFlex }]}>
          <Text>{formatCurrency(channel.price, providerCurrency, true) || '-'}</Text>
        </View>
      )}

      {filterOptions.showDescription && (
        <View style={[styles.tableCell, { flex: descriptionColumnFlex }]}>
          <Text style={{ flexWrap: 'wrap' }}>{(channel.description && channel.description.trim()) || '-'}</Text>
        </View>
      )}
      
      {filterOptions.showPlans && (
        <View style={[styles.tableCell, { flex: plansColumnFlex }]}>
          {(!channel.plans || channel.plans.length === 0) ? (
            <Text style={styles.unassigned}>Unassigned</Text>
          ) : (
            <View style={{ flexDirection: 'row' }}>
              {channel.plans.map(({ plan }, index) => {
                if (!plan) return null;
                return (
                  <View
                    key={`${channel.id}-${plan.id}-${index}`}
                    style={[
                      styles.planIndicator,
                      {
                        backgroundColor: getPlanColor(plan.color_index).light,
                        borderColor: getPlanColor(plan.color_index).dark,
                        marginRight: index < (channel.plans?.length || 0) - 1 ? 4 : 0
                      }
                    ]}
                  />
                );
              })}
            </View>
          )}
        </View>
      )}
    </View>
  );

  // Return early if no valid channels
  if (sortedChannels.length === 0) {
    return (
      <View style={styles.tableContainer}>
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]} wrap={false} fixed>
            <View style={[styles.tableCell, { flex: 1 }]}>
              <Text style={styles.headerText}>No channels available</Text>
            </View>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tableContainer}>
      <View style={styles.table}>
        {/* Table Header - Add fixed prop */}
        <View style={[styles.tableRow, styles.tableHeader]} wrap={false} fixed>
          <View style={[styles.tableCell, { flex: nameColumnFlex }]}>
            <Text style={styles.headerText}>Channel Name</Text>
          </View>

          {/* Show channel types after channel name, filtered based on hideSerialNumbers setting */}
          {displayTypes.map(type => (
            <View key={type.id} style={[styles.tableCell, { flex: numberColumnsFlex }]}>
              <Text style={styles.headerText}>{type.name || 'Type'}</Text>
            </View>
          ))}
          
          {filterOptions.showPrice && (
            <View style={[styles.tableCell, { flex: priceColumnFlex }]}>
              <Text style={styles.headerText}>Price</Text>
            </View>
          )}
          
          {filterOptions.showDescription && (
            <View style={[styles.tableCell, { flex: descriptionColumnFlex }]}>
              <Text style={styles.headerText}>Description</Text>
            </View>
          )}
          
          {filterOptions.showPlans && (
            <View style={[styles.tableCell, { flex: plansColumnFlex }]}>
              <Text style={styles.headerText}>Plans</Text>
            </View>
          )}
        </View>

        {/* Table Body */}
        {sortedChannels.map(channel => (
          <ChannelRow key={channel.id} channel={channel} />
        ))}
      </View>
    </View>
  );
}
